import { Collapse, Divider, Flex, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  ACCEPT_ACTION,
  BOOKED_LESSONS_STAGE,
  PENDING_RESPONSE_STAGE,
  SUBMITTED_FEEDBACK_STAGE,
  LOST_REQUESTS_STATUS,
} from "../stores/jobs/utils/request";
import { scrollToId } from "../utils/functions";
import BookingActions from "./BookingActions";
import BookingCardHeader from "./BookingCardHeader";
import BookingDetails from "./BookingDetails";
import CallResponseSummary from "./CallResponseSummary";
import BookedLessonsSummary from "./BookedLessonsSummary";
import LessonInformation from "./LessonInformation";
import TrackBookingStage from "./TrackBookingStage";
import TutorResponseSummary from "./TutorResponseSummary";
import { IServerRequestInfo } from "../stores";

type Props = {
  requestSLUG: string;
  requestStatus: string;
  tutorResponse: any;
  dateCreated: any;
  tutorInfo: any;
  bookingStage: any;
  customerInfo: any;
  students: Array<any>;
  onSetSchedule: () => void;
  searchSubject: string;
  lessonInfo: any;
  store: IServerRequestInfo;
  billingInfo: any;
  openModal: (action: string) => void;
  assignedSupport: any;
  workingAgent: any;
  host: string;
};

const BookingCard = observer(
  ({
    requestSLUG,
    requestStatus,
    tutorResponse,
    dateCreated,
    tutorInfo = {},
    bookingStage = {},
    customerInfo: client = {},
    students = [],
    onSetSchedule = () => {},
    searchSubject,
    lessonInfo = {},
    store,
    billingInfo = {},
    openModal = () => {},
    assignedSupport = {},
    workingAgent,
    host,
    ...rest
  }: Props) => {
    const [checkedStatus, setCheckedStatus] = React.useState({});
    const [isInvalid, setIsInvalid] = React.useState(false);
    const hasAccepted = store.hasAccepted;
    const isPending = store.isPending;
    const showBreakdown = store.showBreakdown;

    const [show, setShow] = React.useState(isPending);
    const handleShow = () => setShow(!show);

    const canBookLessons =
      bookingStage.current === SUBMITTED_FEEDBACK_STAGE ||
      bookingStage.current === BOOKED_LESSONS_STAGE;

    const lostRequest = LOST_REQUESTS_STATUS.includes(requestStatus);
    const hasBookedLessons = bookingStage.current === BOOKED_LESSONS_STAGE;

    const handleClick = (action: string) => {
      if (action === ACCEPT_ACTION) {
        let checkedValues = Object.values(checkedStatus);
        if (hasAccepted) {
          setIsInvalid(false);
          openModal(action);
          setShow(false);
          return;
        }
        if (checkedValues.filter(Boolean).length === students.length) {
          setIsInvalid(false);
          openModal(action);
          // setShow(false);
        } else {
          scrollToId(requestSLUG);
          setIsInvalid(true);
        }
      } else openModal(action);
    };

    const handleChange = ({ target }: any, index: any) => {
      let newStatus = { ...checkedStatus, [index]: target.checked };
      let checkedValues = Object.values(checkedStatus);
      setCheckedStatus(newStatus);
      if (checkedValues.filter(Boolean).length === students.length) {
        setIsInvalid(false);
      }
    };
    if (isPending) {
      // console.log({ isPending, lostRequest, workingAgent });
    }
    return (
      <Flex
        bg="white"
        minH="196px"
        overflow="hidden"
        shadow="fbShadow"
        alignItems="stretch"
        flexShrink={0}
        borderRadius="fbRadius"
        borderWidth="3xs"
        direction="column"
        {...rest}
      >
        <Stack spacing={6} py={6}>
          <BookingCardHeader
            id={requestSLUG}
            client={client}
            requestSLUG={requestSLUG}
            students={students.length}
            billingInfo={billingInfo}
            showBreakdown={showBreakdown}
            openModal={openModal}
            lostRequest={lostRequest}
            dateCreated={dateCreated}
            lessonInfo={lessonInfo}
            requestStatus={requestStatus}
            tutorResponse={store.tutorResponse}
          />

          {!isPending && (
            <Stack spacing={3}>
              <TutorResponseSummary
                store={store}
                show={show}
                handleShow={handleShow}
                lostRequest={lostRequest}
                students={students}
                lessonInfo={lessonInfo}
              />
              {canBookLessons && (
                <Stack mx={[5, 6]}>
                  <CallResponseSummary store={store} />
                </Stack>
              )}
              {hasBookedLessons && (
                <BookedLessonsSummary
                  bookingDuration={store.tutorResponse.bookingDuration}
                  assignedSupport={assignedSupport}
                  host={host}
                />
              )}
            </Stack>
          )}

          {showBreakdown && show && (
            <Collapse in={show}>
              <Stack spacing={6}>
                <BookingDetails
                  isDisabled={hasAccepted}
                  lostRequest={lostRequest}
                  isInvalid={isInvalid}
                  students={students}
                  isPending={isPending}
                  onChange={handleChange}
                  checkedStatus={checkedStatus}
                />

                <LessonInformation
                  mt={2}
                  px={[5, 6]}
                  client={client}
                  {...lessonInfo}
                />
              </Stack>
              {hasAccepted && <Divider py={4} />}
            </Collapse>
          )}

          {isPending && !lostRequest && !workingAgent && (
            <BookingActions
              id="button-actions"
              isPending={isPending}
              dateCreated={dateCreated}
              onClick={handleClick}
            />
          )}

          {hasAccepted && bookingStage.current !== PENDING_RESPONSE_STAGE && (
            <TrackBookingStage
              client={client}
              tutorInfo={tutorInfo}
              students={students}
              store={store}
              requestSLUG={requestSLUG}
              lostRequest={lostRequest}
              onSetSchedule={onSetSchedule}
              canBookLessons={canBookLessons}
              bookingStage={bookingStage}
              lessonInfo={lessonInfo}
              assignedSupport={assignedSupport}
              requestStatus={requestStatus}
              searchSubject={searchSubject}
              openModal={openModal}
            />
          )}
        </Stack>
      </Flex>
    );
  },
);

export default BookingCard;
